import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Mail, Lock, User, VenetianMask } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function Auth() {
  const { login, register, anonymous } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate("/app");
    } catch (err) { toast.error(apiErr(err)); }
    setLoading(false);
  };

  const goAnon = async () => {
    setLoading(true);
    try { await anonymous(); toast.success("Welcome. You're browsing anonymously."); navigate("/app"); }
    catch (err) { toast.error(apiErr(err)); }
    setLoading(false);
  };

  const forgot = async () => {
    if (!email) { toast.error("Enter your email above first, then tap 'Forgot password?'."); return; }
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message || "If that email is registered, a reset link is on its way.");
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#8FB8DE]/20 to-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#4A7c59] text-white"><Leaf strokeWidth={1.8} className="h-5 w-5" /></div>
          <span className="font-head text-2xl font-bold text-[#2C363F]">TaperPath</span>
        </div>
        <div className="rounded-2xl border border-border bg-white p-8 shadow-xl shadow-[#4A7c59]/5">
          <div className="mb-6 flex rounded-full bg-muted p-1">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)}
                className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors duration-200 ${mode === m ? "bg-white text-[#4A7c59] shadow-sm" : "text-muted-foreground"}`}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-sm">Display name (optional)</Label>
                <div className="relative mt-1"><User className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                  <Input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="A name or nickname" className="rounded-xl py-6 pl-10" /></div>
              </div>
            )}
            <div>
              <Label className="text-sm">Email</Label>
              <div className="relative mt-1"><Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <Input data-testid="input-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="rounded-xl py-6 pl-10" /></div>
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <div className="relative mt-1"><Lock className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <Input data-testid="input-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="rounded-xl py-6 pl-10" /></div>
              {mode === "login" && (
                <button type="button" data-testid="forgot-link" onClick={forgot} className="mt-2 text-sm font-medium text-[#4A7c59] hover:underline">Forgot password?</button>
              )}
            </div>
            <Button data-testid="submit-auth" type="submit" disabled={loading} className="w-full rounded-full bg-[#4A7c59] py-6 text-base font-semibold hover:bg-[#3B6347]">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />OR<div className="h-px flex-1 bg-border" /></div>
          <button data-testid="google-btn" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/auth/callback";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }} className="flex w-full items-center justify-center gap-2 rounded-full border border-border bg-white py-3.5 text-sm font-semibold text-[#2C363F] transition-transform duration-300 hover:-translate-y-0.5">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continue with Google
          </button>
          <button data-testid="anonymous-btn" onClick={goAnon} disabled={loading}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#8FB8DE] bg-[#8FB8DE]/10 py-3.5 text-sm font-semibold text-[#2C4763] transition-transform duration-300 hover:-translate-y-0.5">
            <VenetianMask className="h-4 w-4" /> Continue anonymously
          </button>
          <p className="mt-4 text-center text-xs text-muted-foreground">No real name needed. Your health data stays private and can be deleted any time.</p>
        </div>
      </div>
    </div>
  );
}
